import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import classNames from 'classnames';
import { getVolumeState, setVolumeState, VolumeState } from '../../../utils/volumeStorage';

interface ShortsVideoItemProps {
  video: {
    id: string;
    videoId: string;
    shortsMediaId: string;
    likes: number;
    isLiked?: boolean; // 좋아요 상태 추가
  };
  index: number;
  currentVideoIndex: number; // ✅ 현재 활성 비디오 인덱스 추가
  isAutoPlay?: boolean;
  onLike?: (videoId: string) => void;
  onShare?: (videoId: string) => void;
  onPip?: (videoId: string) => void;
  onVideoEnd?: (index: number) => void;
}

/**
 * ShortsMedia용 비디오 아이템 컴포넌트
 */
const ShortsVideoItem: React.FC<ShortsVideoItemProps> = ({
  video,
  index,
  currentVideoIndex, // ✅ 현재 활성 비디오 인덱스
  isAutoPlay = false,
  onLike,
  onShare,
  onPip,
  onVideoEnd,
}) => {
  const vRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const isVideoTransitionRef = useRef<boolean>(false); // 영상 전환 상태 추적
  const [motion, setMotion] = useState(false); // 좋아요 애니메이션 상태
  const [isPlayerReady, setIsPlayerReady] = useState(false); // 플레이어 준비 상태

  // 안전 가드/툴: 플레이어 접근 시 에러 방지
  const bufferingReportedRef = useRef(0); // BUFFERING_TIMEOUT 로그/재시도 과다 방지
  const bufferingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isPlayerUsable = (p: any) => {
    try {
      return !!(p && !p.isDisposed && !p.disposed_ && (!p.el_ || p.el_.isConnected !== false));
    } catch {
      return false;
    }
  };

  const safePaused = (p: any) => {
    try {
      return typeof p?.paused === 'function' ? !!p.paused() : true;
    } catch {
      return true;
    }
  };

  const safeCurrentTime = (p: any) => {
    try {
      return typeof p?.currentTime === 'function' ? Number(p.currentTime()) : 0;
    } catch {
      return 0;
    }
  };

  // ✅ 가상화: 현재 비디오에서 넓은 범위의 비디오만 실제 로드
  const NORMAL_LOAD_RANGE = 3; // 일반 스크롤 시 앞뒤 3개씩 (증가: 2→3)
  const FAST_SCROLL_LOAD_RANGE = 6; // 빠른 스크롤 시 앞뒤 6개씩 (증가: 4→6)

  // 영상 되감기/빨기 감기 간격
  const VIDEO_WINDED_TERMS = 5;

  // ✅ 빠른 스크롤 감지 로직 추가
  const [isFastScrolling, setIsFastScrolling] = useState(false);
  const lastScrollTimeRef = useRef(0);
  const scrollSpeedRef = useRef(0);

  const currentLoadRange = isFastScrolling ? FAST_SCROLL_LOAD_RANGE : NORMAL_LOAD_RANGE;
  const shouldLoadVideo = Math.abs(index - currentVideoIndex) <= currentLoadRange;

  // ✅ 실제 로드 여부 상태 관리
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // 캐시 버스팅을 위한 재로드 키
  const [reloadKey, setReloadKey] = useState(0);

  // ✅ 첫 번째 비디오는 항상 로드 (pinnedVideo 대응)
  const isFirstVideo = index === 0;
  const shouldForceLoad = isFirstVideo || shouldLoadVideo;

  // ✅ 빠른 스크롤 감지
  useEffect(() => {
    const now = Date.now();
    const timeDiff = now - lastScrollTimeRef.current;

    if (timeDiff > 0) {
      const scrollSpeed = Math.abs(index - currentVideoIndex) / timeDiff * 1000;
      scrollSpeedRef.current = scrollSpeed;

      // 스크롤 속도가 빠르면 (1초에 2개 이상 영상 이동) 빠른 스크롤로 판단
      const newIsFastScrolling = scrollSpeed > 2;

      if (newIsFastScrolling !== isFastScrolling) {
        setIsFastScrolling(newIsFastScrolling);
      }
    }

    lastScrollTimeRef.current = now;
  }, [currentVideoIndex, isFastScrolling]);

  // ✅ 범위에 들어오면 비디오 로드, 나가면 언로드
  useEffect(() => {
    if (shouldForceLoad && !isVideoLoaded) {
      // ✅ 강제 리로드를 위한 상태 초기화
      setIsPlayerReady(false);
      setIsVideoLoaded(true);

    } else if (!shouldForceLoad && isVideoLoaded) {
      // 플레이어 리소스 해제 (DOM은 유지)
      if (playerRef.current) {
        try {
          // 진행 중인 버퍼링 타임아웃/카운터 초기화
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          bufferingReportedRef.current = 0;
          // 정지 및 처음으로 리셋
          try { playerRef.current.pause(); } catch {}
          try {
            if (playerRef.current &&
                typeof playerRef.current.currentTime === 'function' &&
                !playerRef.current.isDisposed &&
                !playerRef.current.disposed_ &&
                playerRef.current.readyState &&
                playerRef.current.readyState() > 0) {
              playerRef.current.currentTime(0);
            }
          } catch {}
          // 소스 제거로 디코더/버퍼 반환
          try { playerRef.current.src(''); } catch {}
          // 플레이어 해제
          try { playerRef.current.dispose(); } catch {}
        } catch (error) {
          console.warn(`비디오 ${index} 언로드 중 오류:`, error);
        } finally {
          playerRef.current = null;
        }
      }

      // ✅ vRef도 초기화 (강제 리로드를 위해)
      if (vRef.current) {
        vRef.current = null;
      }

      setIsVideoLoaded(false);
      setIsPlayerReady(false);
    }
  }, [shouldForceLoad, isVideoLoaded, index, currentVideoIndex, currentLoadRange, isFirstVideo]);

  // 현재 활성 비디오가 변경되었을 때 포커스 설정
  useEffect(() => {
    if (index === currentVideoIndex && isPlayerReady && playerRef.current) {
      try {
        const player = playerRef.current;
        if (player.el_) {
          // 모든 다른 플레이어들의 tabindex 제거
          const allPlayers = document.querySelectorAll('.brightcove-react-player-loader [tabindex]');
          allPlayers.forEach(el => {
            if (el !== player.el_) {
              el.removeAttribute('tabindex');
            }
          });

          // 현재 플레이어에만 포커스 가능하도록 설정
          player.el_.setAttribute('tabindex', '0');
          player.el_.setAttribute('aria-label', `활성 숏폼 비디오 ${index + 1}`);
          player.el_.style.outline = 'none';

          // 지연 후 포커스 설정
          setTimeout(() => {
            try {
              if (player.el_ && player.el_.isConnected) {
                // 현재 포커스된 요소 해제
                const activeElement = document.activeElement;
                if (activeElement && activeElement !== player.el_) {
                  (activeElement as HTMLElement).blur?.();
                }

                // 강제 포커스
                player.el_.focus({ preventScroll: true });
              }
            } catch (error) {
              console.warn(`비디오 ${index} 포커스 재설정 중 오류:`, error);
            }
          }, 300); // 더 긴 지연으로 안정성 확보
        }
      } catch (error) {
        console.warn(`비디오 ${index} 현재 비디오 포커스 설정 중 오류:`, error);
      }
    } else if (index !== currentVideoIndex && playerRef.current?.el_) {
      // 비활성 비디오는 포커스 불가능하도록 설정
      playerRef.current.el_.removeAttribute('tabindex');

      //영상 전환 시, 비활성되면 재생 시간 0으로 셋팅
      if (playerRef.current &&
        typeof playerRef.current.currentTime === 'function') {
        playerRef.current.currentTime(0);
      }
    }
  }, [currentVideoIndex, index, isPlayerReady]);

  // isAutoPlay 변경에 따라 비디오 재생/정지 제어 (Brightcove player 기준)
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !isVideoLoaded) return;

    // 세션 볼륨 상태 재적용 (예외 안전)
    try {
      const st = getVolumeState();
      if (typeof player.muted === 'function') {
        if (player.muted() !== st.muted) player.muted(st.muted);
      }
      if (typeof player.volume === 'function') {
        if (Math.abs(player.volume() - st.volume) > 0.01) player.volume(st.volume);
      }
    } catch {}

    const playOnceReady = () => {
      try {
        const p = player.play();
        if (p && typeof p.catch === 'function') {
          p.catch((err: any) => {
            if (err && err.name !== 'AbortError') {
              console.warn('비디오 자동 재생 실패:', err);
            }
          });
        }

        // 재생 시작 후 10초 내에 'playing' 이벤트가 발생하지 않으면 문제가 있다고 판단
        const playingTimeout = setTimeout(() => {
          if (isPlayerUsable(player) && !safePaused(player)) {
            // readyState 체크 - 0: HAVE_NOTHING, 1: HAVE_METADATA, 2: HAVE_CURRENT_DATA, 3: HAVE_FUTURE_DATA, 4: HAVE_ENOUGH_DATA
            const readyState = typeof player.readyState === 'function' ? player.readyState() : 0;

            // 재생 중인데도 실제로는 멈춰있는 상태 감지
            const currentTime1 = safeCurrentTime(player);
            setTimeout(() => {
              const currentTime2 = safeCurrentTime(player);
              const timeDiff = Math.abs(currentTime2 - currentTime1);

              // 시간이 거의 변하지 않거나 readyState가 너무 낮으면 문제 상황
              if (timeDiff < 0.1 || readyState < 2) {
                console.warn(`비디오 ${index} 재생이 멈춘 상태 감지 - 재로드 시도 (timeDiff: ${timeDiff}, readyState: ${readyState})`);
                handlePlayerFailure({
                  type: 'PLAYBACK_STALLED',
                  message: `Video playback stalled - timeDiff: ${timeDiff}, readyState: ${readyState}`
                });
              }
            }, 3000); // 3초 후 다시 체크
          }
        }, 10000);

        // playing 이벤트 발생 시 타임아웃 클리어
        const clearPlayingTimeout = () => {
          clearTimeout(playingTimeout);
        };
        player.one('playing', clearPlayingTimeout);

      } catch (err) {
        console.warn('비디오 재생 중 예외:', err);
      }
    };

    if (isAutoPlay) {
      // 플레이어가 이미 준비됐다면 즉시 재생, 아니면 준비 이벤트를 한 번만 듣고 재생
      if (isPlayerReady) {
        playOnceReady();
      } else if (typeof player.one === 'function') {
        const onReady = () => {
          try {
            player.off('canplay', onReady);
            player.off('loadedmetadata', onReady);
          } catch {}
          playOnceReady();
        };
        player.one('canplay', onReady);
        player.one('loadedmetadata', onReady);
      }
    } else {
      // 비활성: 항상 정지 후 0초로 초기화 (되돌아왔을 때 처음부터)
      try { player.pause(); } catch {}
      setTimeout(() => {
        const p2 = playerRef.current;
        if (!p2) return;
        try {
          if (p2 &&
              typeof p2.currentTime === 'function' &&
              !p2.isDisposed &&
              !p2.disposed_ &&
              p2.readyState &&
              p2.readyState() > 0) {
            p2.currentTime(0);
          }
        } catch {}
      }, 50);
    }
  }, [isAutoPlay, isPlayerReady, isVideoLoaded]);

  // 컴포넌트 언마운트 시 플레이어 정리
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          try { playerRef.current.off(); } catch {}
          try { playerRef.current.pause(); } catch {}
          try {
            if (playerRef.current &&
                typeof playerRef.current.currentTime === 'function' &&
                !playerRef.current.isDisposed &&
                !playerRef.current.disposed_ &&
                playerRef.current.readyState &&
                playerRef.current.readyState() > 0) {
              playerRef.current.currentTime(0);
            }
          } catch {}
          try { playerRef.current.src(''); } catch {}
          try { playerRef.current.dispose(); } catch {}
          playerRef.current = null;
        } catch (error) {
          console.warn('❌ Brightcove 플레이어 정리 중 오류:', error);
          // 오류가 발생해도 참조는 초기화
          playerRef.current = null;
        }
      }

      // ✅ vRef도 정리
      if (vRef.current) {
        try {
          vRef.current = null;
        } catch (error) {
          console.warn('vRef 정리 중 오류:', error);
        }
      }
    };
  }, []);

  // 애니메이션 상태 리셋
  useEffect(() => {
    if (motion) {
      const timer = setTimeout(() => {
        setMotion(false);
      }, 500); // 애니메이션 시간과 맞춤
      return () => clearTimeout(timer);
    }
  }, [motion]);

  const handleLike = useCallback(() => {
    if (onLike) {
      setMotion(true);
      // 이미 좋아요한 상태가 아닐 때만 API 호출
      if (!video.isLiked) {
        onLike(video.id);
      }
    }
  }, [onLike, video.id, video.isLiked]);

  const handleShare = useCallback(() => {
    if (onShare) {
      onShare(video.id);
    }
  }, [onShare, video.id]);

  const handlePip = useCallback(() => {
    if (onPip) {
      onPip(video.id);
    }
  }, [onPip, video.id]);

  // 플레이어 실패 핸들러 - 강화된 재시도 로직
  const retryCountRef = useRef(0);
  const MAX_RETRY_COUNT = 3;

  const handlePlayerFailure = useCallback((error: any) => {
    console.warn(`비디오 ${index} ReactPlayerLoader 실패 (${retryCountRef.current + 1}/${MAX_RETRY_COUNT}):`, error);
    setIsPlayerReady(false);

    // 네트워크 상태 체크
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (!isOnline) {
      console.warn(`비디오 ${index} 오프라인 상태로 재시도 중단`);
      return;
    }

    // MEDIA_ERR_SRC_NOT_SUPPORTED 또는 네트워크 오류 시 재시도
    if (shouldForceLoad && retryCountRef.current < MAX_RETRY_COUNT) {
      retryCountRef.current += 1;
      const retryDelay = 1000 * retryCountRef.current; // 점진적 재시도 지연

      setTimeout(() => {
        setIsVideoLoaded(false);
        // 캐시 버스팅을 위한 key 강제 변경
        setReloadKey(prev => prev + 1);
        setTimeout(() => {
          if (shouldForceLoad) {
            setIsVideoLoaded(true);
          }
        }, 200);
      }, retryDelay);
    } else {
      console.error(`비디오 ${index} 최대 재시도 횟수 초과 또는 로드 불필요`);
      retryCountRef.current = 0;
    }
  }, [index, shouldForceLoad]);

  const onSuccess = (success: any) => {
    try {
      const player = success.ref;

      // 플레이어 인스턴스 확인만 수행 (el_ 연결은 브라우저 타이밍에 따라 지연될 수 있음)
      if (!player) {
        return;
      }

      playerRef.current = player;

      // 브라우저 autoplay 정책에 맞게 muted 설정
      // 세션 스토리지에서 볼륨 상태 가져와서 적용
      try {
        const volumeState = getVolumeState();
        if (typeof player.muted === 'function') {
          player.muted(volumeState.muted);
        }
        if (typeof player.volume === 'function') {
          player.volume(volumeState.volume);
        }
      } catch (error) {
        console.warn(`비디오 ${index} 볼륨 상태 설정 중 오류:`, error);
      }

      // 비디오 종료 이벤트 리스너 추가
      try {
        if (typeof player.on === 'function') {
          player.on('ended', () => {
            if (onVideoEnd) {
              onVideoEnd(index);
            }
          });
        }
      } catch (error) {
        console.warn(`비디오 ${index} ended 이벤트 리스너 추가 중 오류:`, error);
      }

      // 볼륨 변경 이벤트 리스너 추가 - 세션스토리지에만 저장
      try {
        if (typeof player.on === 'function') {
          player.on('volumechange', () => {
            try {
              const isMuted: boolean = typeof player.muted === 'function' ? player.muted() : false;
              const currentVolume: number = typeof player.volume === 'function' ? player.volume() : 1;

              const newVolumeState: VolumeState = {
                muted: isMuted,
                volume: currentVolume
              };

              // 세션스토리지에 저장
              setVolumeState(newVolumeState);
            } catch (error) {
              console.warn(`비디오 ${index} 볼륨 변경 처리 중 오류:`, error);
            }
          });
        }
      } catch (error) {
        console.warn(`비디오 ${index} volumechange 이벤트 리스너 추가 중 오류:`, error);
      }

      // 음소거 상태 변경도 함께 감지 (일부 브라우저에서 volumechange가 발생하지 않을 수 있음)
      try {
        if (typeof player.on === 'function') {
          player.on('play', () => {
            try {
              // 재생 시에도 세션스토리지의 볼륨 상태 재적용
              const currentVolumeState = getVolumeState();
              if (typeof player.muted === 'function' && player.muted() !== currentVolumeState.muted) {
                player.muted(currentVolumeState.muted);
              }
              if (typeof player.volume === 'function' && Math.abs(player.volume() - currentVolumeState.volume) > 0.01) {
                player.volume(currentVolumeState.volume);
              }
            } catch (error) {
              console.warn(`비디오 ${index} 재생 시 처리 중 오류:`, error);
            }
          });
        }
      } catch (error) {
        console.warn(`비디오 ${index} play 이벤트 리스너 추가 중 오류:`, error);
      }

      // 미디어 오류 이벤트 리스너 추가 - MEDIA_ERR_SRC_NOT_SUPPORTED 등 처리
      try {
        if (typeof player.on === 'function') {
          player.on('error', (errorEvent: any) => {
            const mediaError = player.error();
            // MEDIA_ERR_SRC_NOT_SUPPORTED (4) 또는 MEDIA_ERR_NETWORK (2) 처리
            if (mediaError && (mediaError.code === 4 || mediaError.code === 2)) {
              handlePlayerFailure({
                type: 'MEDIA_ERROR',
                code: mediaError.code,
                message: mediaError.message
              });
            }
          });

          // 'waiting' 이벤트로 버퍼링 감지
          player.on('waiting', () => {
            if (!isPlayerUsable(player)) return;
            if (bufferingTimeoutRef.current) {
              clearTimeout(bufferingTimeoutRef.current);
              bufferingTimeoutRef.current = null;
            }
            // 10초 후에도 버퍼링이 끝나지 않으면 재로드 시도 (로그 스팸 방지)
            bufferingTimeoutRef.current = setTimeout(() => {
              if (!isPlayerUsable(player)) return;
              if (bufferingReportedRef.current < 1) {
                console.warn(`비디오 ${index} 버퍼링 타임아웃 - 재로드 시도`);
                bufferingReportedRef.current += 1;
              }
              handlePlayerFailure({
                type: 'BUFFERING_TIMEOUT',
                message: 'Video stuck in buffering state'
              });
            }, 10000);
          });

          // 'canplay' 또는 'playing' 이벤트로 버퍼링 완료 감지
          const clearBufferingTimeout = () => {
            if (bufferingTimeoutRef.current) {
              clearTimeout(bufferingTimeoutRef.current);
              bufferingTimeoutRef.current = null;
            }
          };

          player.on('canplay', clearBufferingTimeout);
          player.on('playing', clearBufferingTimeout);

          // 'stalled' 이벤트로 네트워크 문제 감지
          player.on('stalled', () => {
            if (!isPlayerUsable(player)) return;
            if (bufferingReportedRef.current < 2) {
              console.warn(`비디오 ${index} 네트워크 지연 감지`);
              bufferingReportedRef.current += 1;
            }
            setTimeout(() => {
              if (!isPlayerUsable(player)) return;
              if (player.networkState && player.networkState() === 2) {
                handlePlayerFailure({
                  type: 'NETWORK_STALLED',
                  message: 'Network stalled for too long'
                });
              }
            }, 5000);
          });

          // 'seeked' 이벤트로 사용자 탐색 완료 감지
          player.on('seeked', () => {
            setTimeout(() => {
              if (isPlayerUsable(player) && !safePaused(player)) {
                const startTime = safeCurrentTime(player);

                setTimeout(() => {
                  const endTime = safeCurrentTime(player);
                  if (Math.abs(endTime - startTime) < 0.1) {
                    console.warn(`비디오 ${index} 탐색 후 재생 정지 - 복구 시도`);

                    // 간단한 복구 시도 (pause -> play)
                    try {
                      player.pause();
                      setTimeout(() => {
                        if (isAutoPlay) {
                          player.play().catch(() => {});
                        }
                      }, 200);
                    } catch (error) {
                      console.warn(`비디오 ${index} 복구 시도 중 오류:`, error);
                    }
                  }
                }, 2000);
              }
            }, 1000);
          });
        }
      } catch (error) {
        console.warn(`비디오 ${index} error 이벤트 리스너 추가 중 오류:`, error);
      }

      // VideoJS 스타일 커스터마이징 - 타임라인 툴팁 가로 정렬
      try {
        // 플레이어 엘리먼트에 커스텀 스타일 추가
        const playerEl = player.el_;
        if (playerEl) {
          // 기존 스타일 태그가 있는지 확인
          const existingStyle = playerEl.querySelector('#brightcove-custom-style');
          if (!existingStyle) {
            const style = document.createElement('style');
            style.id = 'brightcove-custom-style';
            style.textContent = `
              /* 타임라인 호버 툴팁 가로 정렬 및 중앙 정렬 */
              .vjs-mouse-display .vjs-time-tooltip {
                position: absolute !important;
                white-space: nowrap !important;
                width: auto !important;
                height: auto !important;
                min-width: 40px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                text-align: center !important;
                line-height: 1 !important;
                padding: 6px 10px !important;
                border-radius: 4px !important;
                background: rgba(0, 0, 0, 0.85) !important;
                color: white !important;
                font-size: 12px !important;
                font-weight: 500 !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
                transform: translateX(-50%) !important;
                left: 50% !important;
                bottom: calc(100% + 8px) !important;
                z-index: 1000 !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
              }
              
              /* 프로그레스 바 호버 영역 */
              .vjs-progress-control:hover .vjs-mouse-display {
                display: block !important;
              }
              
              /* 시간 툴팁 화살표 */
              .vjs-mouse-display .vjs-time-tooltip::after {
                content: '' !important;
                position: absolute !important;
                top: 100% !important;
                left: 50% !important;
                transform: translateX(-50%) !important;
                width: 0 !important;
                height: 0 !important;
                border: 5px solid transparent !important;
                border-top-color: rgba(0, 0, 0, 0.85) !important;
                border-bottom: none !important;
              }
              
              /* 마우스 디스플레이 컨테이너 최적화 */
              .vjs-mouse-display {
                position: absolute !important;
                z-index: 999 !important;
              }
            `;
            playerEl.appendChild(style);
          }
        }
      } catch (error) {
        console.warn(`비디오 ${index} 커스텀 스타일 적용 중 오류:`, error);
      }

      // 플레이어 준비 완료 상태 설정
      setIsPlayerReady(true);

      // 성공적으로 로드되면 재시도 카운터 리셋
      retryCountRef.current = 0;

      // 🎯 숏폼 특성: 영상 전환 시에만 처음부터 재생 (같은 영상 내 정지/재생은 이어서)
      // 더 정확한 영상 전환 감지: 새로운 플레이어이거나 인덱스가 바뀐 경우
      const isVideoTransition = !playerRef.current ||
                                playerRef.current !== player ||
                                (playerRef.current === player && currentVideoIndex !== index);

      // ref에 영상 전환 상태 저장 (다른 이벤트에서도 참조 가능)
      isVideoTransitionRef.current = isVideoTransition;

      // 영상 전환인 경우 playerRef 업데이트
      if (isVideoTransition) {
        playerRef.current = player;
      }

      // 강력한 다중 리셋 방식 적용
      const forceResetToZero = () => {
        try {
          // VideoJS currentTime 리셋
          if (typeof player.currentTime === 'function') {
            player.currentTime(0);
          }

          // HTML5 비디오 엘리먼트 직접 리셋
          const videoEl = player.el_?.querySelector('video');
          if (videoEl) {
            videoEl.currentTime = 0;
          }

          // Brightcove 플레이어 내부 시간 추적 무력화
          if (player.cache_ && player.cache_.currentTime !== undefined) {
            player.cache_.currentTime = 0;
          }

          // 추가 보안: 모든 video 태그 강제 리셋
          const allVideos = player.el_?.querySelectorAll('video');
          if (allVideos && allVideos.length > 0) {
            allVideos.forEach((video: HTMLVideoElement, idx: number) => {
              video.currentTime = 0;
            });
          }
        } catch (error) {
          console.warn(`비디오 ${index} 강력 리셋 중 오류:`, error);
        }
      };

      // 영상 전환 시에만 리셋 실행
      if (isVideoTransition) {
        // 영상 준비 상태 확인
        if(player.isReady_) {
          forceResetToZero();
        }
      } else {
        // ▶️ 비디오 ${index} 기존 영상 재시작 - 이어서 재생 (리셋 안함)
      }

      // 스페이스바 제어를 위한 포커스 및 키보드 설정
      try {
        // VideoJS 키보드 지원 활성화
        if (typeof player.ready === 'function') {
          player.ready(() => {
            // VideoJS hotkeys 플러그인이 있다면 활성화
            if (player.hotkeys && typeof player.hotkeys === 'function') {
              player.hotkeys({
                volumeStep: 0.1,
                seekStep: 5,
                enableMute: true,
                enableFullscreen: true,
                enableNumbers: false,
                enableModifiersForNumbers: false
              });
            }

            // 플레이어에 기본 키보드 이벤트 리스너 추가
            if (player.el_) {
              player.el_.addEventListener('keydown', (e: KeyboardEvent) => {
                // 스페이스바 처리 - 여러 조건으로 체크
                if (e.key === ' ' || e.key === 'Space' || e.code === 'Space' || e.which === 32 || e.keyCode === 32) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();

                  // 현재 활성 비디오가 아니면 무시
                  if (index !== currentVideoIndex) {
                    return false;
                  }

                  if (isPlayerUsable(player) && typeof player.play === 'function' && typeof player.pause === 'function') {
                    try {
                      const isPaused = safePaused(player);

                      if (isPaused) {
                        // 재생 전 상태 확인
                        if (player.readyState && typeof player.readyState === 'function' && player.readyState() < 2) {
                          console.warn(`비디오 ${index} readyState가 낮음 (${player.readyState()}) - 재생 불가능`);
                          return false;
                        }

                        // 다중 접근법으로 재생 시도
                        try {
                          // 1. VideoJS trigger를 통한 play 이벤트 발생
                          if (typeof player.trigger === 'function') {
                            player.trigger('play');
                          }

                          // 2. VideoJS play 메소드 직접 호출
                          const playPromise = player.play();
                          if (playPromise && typeof playPromise.then === 'function') {
                            playPromise
                              .then(() => {
                                // 재생 이벤트도 트리거
                                if (typeof player.trigger === 'function') {
                                  player.trigger('playing');
                                }
                              })
                              .catch((error: any) => {
                                console.warn(`비디오 ${index} VideoJS 재생 실패:`, error);
                                // 3. HTML5 비디오 엘리먼트 직접 제어
                                const videoEl = player.el_?.querySelector('video');
                                if (videoEl) {
                                  videoEl.play().catch(() => {});
                                }
                              });
                          } else {
                            // Promise가 아닌 경우 HTML5 비디오 엘리먼트 직접 제어
                            const videoEl = player.el_?.querySelector('video');
                            if (videoEl) {
                              videoEl.play().catch(() => {});
                            }
                          }
                        } catch (playError) {
                          console.warn(`비디오 ${index} play() 호출 실패:`, playError);
                          // 최후 수단: HTML5 비디오 엘리먼트 직접 제어
                          const videoEl = player.el_?.querySelector('video');
                          if (videoEl) {
                            videoEl.play().catch(() => {});
                          }
                        }
                      } else {
                        // 다중 접근법으로 일시정지 시도
                        try {
                          // 1. VideoJS trigger를 통한 pause 이벤트 발생
                          if (typeof player.trigger === 'function') {
                            player.trigger('pause');
                          }

                          // 2. VideoJS pause 메소드 호출
                          player.pause();

                          // 3. HTML5 비디오 엘리먼트도 직접 일시정지
                          const videoEl = player.el_?.querySelector('video');
                          if (videoEl) {
                            videoEl.pause();
                          }

                        } catch (pauseError) {
                          console.warn(`비디오 ${index} pause() 호출 실패:`, pauseError);
                          // 최후 수단: HTML5 비디오 엘리먼트 직접 제어
                          const videoEl = player.el_?.querySelector('video');
                          if (videoEl) {
                            videoEl.pause();
                          }
                        }
                      }
                    } catch (error) {
                      console.warn(`비디오 ${index} 재생/정지 처리 중 오류:`, error);
                    }
                  } else {
                    console.warn(`비디오 ${index} 플레이어 메소드 사용 불가`);
                  }

                  return false;
                }
              });
            }
          });
        }

        // 현재 활성 비디오일 때 포커스 설정
        if (index === currentVideoIndex && player.el_) {
          player.el_.setAttribute('tabindex', '0');
          player.el_.setAttribute('aria-label', `숏폼 비디오 ${index + 1}`);

          // CSS로 포커스 아웃라인 제거 (사용자 경험 개선)
          player.el_.style.outline = 'none';

          // 약간의 지연 후 포커스 설정 (DOM 안정화 대기)
          setTimeout(() => {
            try {
              if (player.el_ && player.el_.isConnected) {
                // 다른 포커스된 요소들 해제
                const activeElement = document.activeElement;
                if (activeElement && activeElement !== player.el_) {
                  (activeElement as HTMLElement).blur?.();
                }

                player.el_.focus({ preventScroll: true }); // 스크롤 방지
                // `비디오 ${index} 포커스 및 키보드 제어 설정 완료`
              }
            } catch (focusError) {
              console.warn(`비디오 ${index} 포커스 설정 중 오류:`, focusError);
            }
          }, 200); // 지연 시간 증가
        }
      } catch (error) {
        console.warn(`비디오 ${index} 키보드 제어 설정 중 오류:`, error);
      }

      // 초기 autoPlay 상태에 따라 재생/정지 결정 (안전 처리)
      if (isAutoPlay) {
        try {
          if (isVideoTransitionRef.current) {
            // 🎯 영상 전환 시에만 0초부터 시작하도록 강력 리셋
            forceResetToZero();

            // 🎯 스마트 모니터링: 처음 몇 초만 감시하고, 사용자 조작은 허용
            let resetAttempts = 0;
            let lastUserInteractionTime = 0;
            const maxResetAttempts = 5;
            const monitoringTimeLimit = 3000;
            const startTime = Date.now();

            // 사용자 조작 감지 (seeking 이벤트)
            const onSeeking = () => {
              lastUserInteractionTime = Date.now();
            };

            if (typeof player.on === 'function') {
              player.on('seeking', onSeeking);
            }

            const timeUpdateHandler = () => {
              try {
                const now = Date.now();

                // 모니터링 시간 초과 또는 최대 시도 횟수 도달 시 리스너 제거
                if (now - startTime > monitoringTimeLimit || resetAttempts >= maxResetAttempts) {
                  if (typeof player.off === 'function') {
                    player.off('timeupdate', timeUpdateHandler);
                    player.off('seeking', onSeeking);
                  }
                  return;
                }

                const currentTime = safeCurrentTime(player);

                // 사용자 조작 후 1초 이내는 리셋하지 않음
                if (now - lastUserInteractionTime < 1000) {
                  return;
                }

                // 초기 로드 후 0.5초 이상에서 재생 시작 시에만 리셋 (자동 이어보기만 차단)
                if (currentTime > 0.5 && resetAttempts < maxResetAttempts && now - startTime < 2000) {
                  console.warn(`🎯 비디오 ${index} 자동 이어보기 감지! ${currentTime.toFixed(2)}초 -> 0초로 이동 (${resetAttempts + 1}/${maxResetAttempts})`);
                  forceResetToZero();
                  resetAttempts++;
                } else if (currentTime <= 0.5 && resetAttempts === 0) {
                  // 정상적으로 0초부터 시작하는 경우 조기 종료
                  if (typeof player.off === 'function') {
                    player.off('timeupdate', timeUpdateHandler);
                    player.off('seeking', onSeeking);
                  }
                }
              } catch (error) {
                console.warn(`비디오 ${index} 스마트 모니터링 중 오류:`, error);
              }
            };

            if (typeof player.on === 'function') {
              player.on('timeupdate', timeUpdateHandler);
            }
          } else {
             // `▶️ 비디오 ${index} 기존 영상 자동재생 - 이어서 재생`
          }

          if (typeof player.play === 'function') {
            const p = player.play();
            if (p && typeof p.catch === 'function') {
              p.catch(() => {});
            }
          }
        } catch (error) {
          console.warn(`비디오 ${index} 초기 재생 중 오류:`, error);
        }
      } else {
        try {
          if (typeof player.pause === 'function') {
            player.pause();
          }
          // 🎯 영상 전환 시에만 일시정지 상태에서도 리셋 적용
          if (isVideoTransitionRef.current) {
            forceResetToZero();
          } else {
             // `⏸️ 비디오 ${index} 기존 영상 일시정지 - 현재 위치 유지`
          }
        } catch (error) {
          console.warn(`비디오 ${index} 초기 정지/리셋 중 오류:`, error);
        }
      }
    } catch (error) {
      console.error('비디오 플레이어 초기화 중 오류:', error);
    }
  };

  // ✅ 추가: isVideoLoaded 상태 변화 감지 및 강제 리로드
  useEffect(() => {
    if (isVideoLoaded && !isPlayerReady && shouldForceLoad) {
      // 5초 후에도 플레이어가 준비되지 않으면 강제 리로드 (3초→5초로 증가)
      const timeoutId = setTimeout(() => {
        if (!isPlayerReady && isVideoLoaded) {
          console.warn(`⚠️ 비디오 ${index} 로드 타임아웃 - MEDIA_ERR_SRC_NOT_SUPPORTED 가능성으로 재시도`);
          handlePlayerFailure({
            type: 'TIMEOUT',
            message: 'Player loading timeout - possible MEDIA_ERR_SRC_NOT_SUPPORTED'
          });
        }
      }, 5000); // 타임아웃을 5초로 증가

      return () => clearTimeout(timeoutId);
    }
  }, [isVideoLoaded, isPlayerReady, shouldForceLoad, index, handlePlayerFailure]);

  return (
    <>
      <div className="detail_article">
        <div className="detail_article_inner">
          <div className="shorts_list_wrap">
            <div className="detail_video">
              <div className="brightcove">
                {shouldForceLoad && isVideoLoaded ? (
                  <ReactPlayerLoader
                    key={`${video.videoId}-${index}-${isVideoLoaded ? 'on' : 'off'}-${reloadKey}`}
                    accountId="5746988503001"
                    videoId={video.videoId}
                    onSuccess={onSuccess}
                    onFailure={handlePlayerFailure}
                    ref={vRef}
                    options={{ 
                      playback: { 
                        autoplay: false, 
                        muted: true, 
                        volume: 1.0, 
                        playsinline: true,
                        // 재시도 시 캐시 우회를 위한 추가 옵션
                        preload: 'metadata',
                        fluid: true
                      },
                      // 캐시 버스팅
                      embedOptions: {
                        unminified: true,
                        cacheBuster: reloadKey
                      }
                    }}
                  />
                ) : (
                  <div
                    className="video-placeholder"
                    aria-label="video placeholder"
                    style={{ width: '100%', height: '100%', background: '#000' }}
                  />
                )}
              </div>
              
              {/* 모바일 PIP 버튼 */}
              <a 
                href="#;" 
                className="detail_video_pip is-mobile"
                onClick={(e) => {
                  e.preventDefault();
                  handlePip();
                }}
              >
                <img src="/assets/images/icon/ico-pip.png" alt="pip" />
              </a>
              
              {/* PC PIP 호버 컨트롤 */}
              <div className="detail_pip_hover is-pc">
                <div className="detail_pip_hover_dim"></div>
                <a 
                  href="#;" 
                  className="detail_pip_hover_return"
                  onClick={(e) => e.preventDefault()}
                >
                  <img src="/assets/images/icon/ico-pip-return.png" alt="return" />
                </a>
                <a 
                  href="#;" 
                  className="detail_pip_hover_close"
                  onClick={(e) => e.preventDefault()}
                >
                  <img src="/assets/images/icon/ico-close-white.png" alt="close" />
                </a>
                <a 
                  href="#;" 
                  className="detail_pip_hover_play"
                  onClick={(e) => e.preventDefault()}
                >
                  <img
                    src="/assets/images/icon/ico-pip-play.png"
                    alt="play"
                    className="pip_hover_btn is-play"
                  />
                  <img
                    src="/assets/images/icon/ico-pip-pause.png"
                    alt="pause"
                    className="pip_hover_btn is-pause"
                  />
                </a>
                <span className="detail_pip_hover_time">
                  <span className="hover_time_now">0:10</span>/
                  <span className="hover_time_all">0:51</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 비디오 액션 버튼들 (좋아요, 공유) */}
      <ul className="detail_article_sub">
        <li>
          <a 
            href="#;" 
            className={classNames('detail_info_link', 'is-likes', {
              'is-active': video.isLiked === true,
              'is-motion': motion,
            })}
            onClick={(e) => {
              e.preventDefault();
              handleLike();
            }}
          />
          <div className="detail_info_text">{video.likes}</div>
        </li>
        <li>
          <a 
            href="#;" 
            className="detail_info_link is-share"
            onClick={(e) => {
              e.preventDefault();
              handleShare();
            }}
          >
            <img src="/assets/images/icon/ico-info-share.png" alt="share" />
          </a>
          <div className="detail_info_text">
            이메일<br />공유
          </div>
        </li>
      </ul>
    </>
  );
};

export default ShortsVideoItem;